import { useEffect, useState } from 'react'

import { hora as horaDe } from '@/modules/tablero/semana'

import estilos from './RelojDelTrabajo.module.css'

type Props = {
  inicioProgramado: string | null
  finProgramado: string | null
  horasAsignadas: number | null
  /** Cuándo llegó de verdad. Nulo si todavía no ha llegado. */
  llegada: string | null
  /**
   * Ya terminó y está esperando que la oficina lo revise.
   *
   * Sin esto el reloj volvía a «Falta para iniciar»: al marcar salida se
   * pierde la llegada, y el componente leía eso como «todavía no ha
   * empezado». Juan Diego terminó la OT-2026-0048, y su teléfono le dijo que
   * le faltaban 16 horas para arrancar el trabajo que acababa de entregar.
   */
  terminado?: boolean
  /** Lo que de verdad estuvo, para poder enseñarlo cuando ya terminó. */
  horasEjecutadas?: number | null
  /** Marcar la salida a mano, sin esperar a que el GPS la detecte. */
  onTerminar: () => void
  terminando: boolean
  puedeTerminar: boolean
  /**
   * Sin marco propio: dentro de la tarjeta del técnico el reloj es una franja
   * de la tarjeta, no otra caja encima. El rojo del «se pasó» queda en la
   * cifra.
   */
  plano?: boolean
}

/**
 * El reloj del trabajo, como lo necesita el operario.
 *
 * Tres cosas y nada más: a qué hora tenía que ser, a qué hora llegó, y cuánto
 * le queda. Es la pantalla que mira de pie, con el cliente al lado, así que
 * la cifra que importa —lo que queda— va grande y sola.
 *
 * Cuando se pasa de la hora, el número no desaparece ni se pone en cero: se
 * vuelve negativo y rojo. Saber que va cuarenta minutos tarde es más útil que
 * ver un cero que no dice nada.
 */

/** Se refresca cada medio minuto: un contador al segundo no aporta y gasta. */
const TICTAC_MS = 30_000

function formatear(minutos: number): string {
  const signo = minutos < 0 ? '−' : ''
  const totales = Math.abs(Math.round(minutos))
  const horas = Math.floor(totales / 60)
  const resto = totales % 60

  if (horas === 0) return `${signo}${resto} min`
  if (resto === 0) return `${signo}${horas} h`
  return `${signo}${horas} h ${resto} min`
}

/**
 * «1:59 – 5:59 p. m.» y no «1:59 p. m. a 5:59 p. m.»: si las dos horas son de
 * la misma mitad del día, el «p. m.» va una vez. En el teléfono la versión
 * larga se partía en tres renglones.
 */
function rango(inicio: string | null, fin: string | null): string {
  if (!inicio) return '—'
  const a = horaDe(inicio)
  if (!fin) return a
  const b = horaDe(fin)
  const mitad = /\s*[ap]\.\s*m\.$/i
  const ma = a.match(mitad)?.[0]
  const mb = b.match(mitad)?.[0]
  if (ma && mb && ma.replace(/\s/g, '') === mb.replace(/\s/g, '')) {
    return `${a.replace(mitad, '')} – ${b}`
  }
  return `${a} – ${b}`
}

export function RelojDelTrabajo({
  inicioProgramado,
  finProgramado,
  horasAsignadas,
  llegada,
  terminado = false,
  horasEjecutadas = null,
  onTerminar,
  terminando,
  puedeTerminar,
  plano = false,
}: Props) {
  const [ahora, setAhora] = useState(() => Date.now())

  useEffect(() => {
    const reloj = setInterval(() => setAhora(Date.now()), TICTAC_MS)
    return () => clearInterval(reloj)
  }, [])

  const inicioMs = inicioProgramado ? new Date(inicioProgramado).getTime() : null
  const finMs = finProgramado ? new Date(finProgramado).getTime() : null
  const llegadaMs = llegada ? new Date(llegada).getTime() : null

  const llevaMin = llegadaMs === null ? null : (ahora - llegadaMs) / 60_000

  // ANTES DE LLEGAR se mira otra cosa: no «cuánto queda del trabajo» —que aún
  // no empieza y confundía—, sino cuánto FALTA para arrancar. Una vez en el
  // sitio, el reloj cambia y pasa a contar lo que queda hasta la hora de fin.
  const enSitio = llegadaMs !== null

  const faltaMin = inicioMs === null ? null : (inicioMs - ahora) / 60_000
  const quedanMin = finMs === null ? null : (finMs - ahora) / 60_000

  // El rojo del «se pasó» solo tiene sentido ya trabajando; antes de llegar, ir
  // tarde para empezar no es lo mismo y no se pinta igual.
  const pasado = !terminado && enSitio && quedanMin !== null && quedanMin < 0

  return (
    <div className={`${estilos.zona} ${plano ? estilos.plano : ''} ${pasado ? estilos.pasado : ''}`}>
      {/* La cifra que se mira de reojo: antes de llegar, lo que falta para
          empezar; ya en el sitio, lo que queda hasta terminar. */}
      <div className={estilos.principal}>
        {terminado ? (
          <>
            <span className={estilos.rotuloGrande}>Terminado · falta revisarlo</span>
            <span className={estilos.cifra}>
              {horasEjecutadas === null ? '—' : formatear(horasEjecutadas * 60)}
            </span>
          </>
        ) : enSitio ? (
          <>
            <span className={estilos.rotuloGrande}>
              {quedanMin === null ? 'Sin hora de fin' : pasado ? 'Se pasó por' : 'Le queda'}
            </span>
            <span className={estilos.cifra}>
              {quedanMin === null ? '—' : formatear(Math.abs(quedanMin))}
            </span>
          </>
        ) : (
          <>
            <span className={estilos.rotuloGrande}>
              {faltaMin === null
                ? 'Sin hora de inicio'
                : faltaMin >= 0
                  ? 'Falta para iniciar'
                  : 'Debió empezar hace'}
            </span>
            <span className={estilos.cifra}>
              {faltaMin === null ? '—' : formatear(Math.abs(faltaMin))}
            </span>
          </>
        )}

        {llevaMin !== null && (
          <span className={estilos.llevando}>Lleva {formatear(llevaMin)} en el sitio</span>
        )}
      </div>

      <div className={estilos.detalles}>
        {/* Lo programado contra lo real, uno debajo del otro para poder
            compararlos sin sacar la cuenta. */}
        <div className={estilos.linea}>
          <span className={estilos.etiqueta}>Programado</span>
          <span className={estilos.valor}>
            {rango(inicioProgramado, finProgramado)}
            {horasAsignadas !== null && (
              <span className={estilos.horas}>{Number(horasAsignadas)} h</span>
            )}
          </span>
        </div>

        <div className={estilos.linea}>
          <span className={estilos.etiqueta}>Llegó</span>
          <span className={`${estilos.valor} ${llegada ? estilos.real : estilos.pendiente}`}>
            {llegada ? horaDe(llegada) : 'todavía no'}
            {llegada && inicioProgramado && (
              <span className={estilos.corrimiento}>
                {(() => {
                  const diff =
                    (new Date(llegada).getTime() - new Date(inicioProgramado).getTime()) / 60_000
                  if (Math.abs(diff) < 10) return 'a tiempo'
                  return diff > 0 ? `${formatear(diff)} tarde` : `${formatear(-diff)} antes`
                })()}
              </span>
            )}
          </span>
        </div>
      </div>

      {puedeTerminar && (
        <button
          type="button"
          className={estilos.terminar}
          onClick={onTerminar}
          disabled={terminando}
        >
          {terminando ? 'Registrando…' : 'Terminar'}
        </button>
      )}
    </div>
  )
}
